import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, Loader2, Send, X } from 'lucide-react';
import { toast } from 'sonner';
import { binder, ApiError, type DocumentVersionRow } from '@/api/client';
import { errorText } from '@/lib/permissionError';

/**
 * Sprint-15 — Multi-approver initiate-approval dialog.
 *
 * Replaces the Sprint-9 single-signer dialog. The coordinator no longer
 * picks signers manually — the platform_admin configured an approver
 * RULE in eISF Master Data, and the BE resolves it at submit time from
 * the catalog junction + the study delegations. This dialog is therefore
 * a confirmation surface: the coordinator picks the workflow name
 * (already passed in as a prop) and confirms — the dialog closes
 * immediately on success, and the resolved approver list is surfaced via
 * a toast rather than a second in-dialog screen.
 *
 * Failure modes the BE may surface:
 *   - No approvers configured for this document type
 *   - A required role has no active holder on the study
 *   - More than 5 approvers (DocuSeal positional slot cap)
 *   - The document type has no DocuSeal template
 * Each comes back as an `ApiError` with a coordinator-friendly message;
 * the toast surfaces it verbatim.
 */

export type InitiateApprovalWorkflowName =
  | 'Send for PI Signature'
  | 'Send for Review'
  | 'Send for eSignature Approval'
  | 'Send for Read and Understand';

export interface InitiateApprovalDialogProps {
  binderId: string;
  documentId: string;
  version: DocumentVersionRow;
  workflowName: InitiateApprovalWorkflowName;
  open: boolean;
  onClose: () => void;
  onInitiated: () => void;
}

export function InitiateApprovalDialog({
  binderId,
  documentId,
  version,
  workflowName,
  open,
  onClose,
  onInitiated,
}: InitiateApprovalDialogProps) {
  const qc = useQueryClient();

  const [internalNote, setInternalNote] = useState('');

  // Reset whenever the dialog re-opens for a different version/workflow.
  useEffect(() => {
    if (open) {
      setInternalNote('');
    }
  }, [open, version.version_id, workflowName]);

  const initiate = useMutation({
    mutationFn: () =>
      binder.initiateApproval(binderId, documentId, version.version_id, {
        workflow_name: workflowName,
      }),
    onSuccess: (data) => {
      const noteSuffix = internalNote.trim() ? ` — note: "${internalNote.trim()}"` : '';
      const count = data.approvers.length;
      const modeLabel =
        data.approval_mode === 'sequential'
          ? 'sequential'
          : data.approval_mode === 'parallel'
            ? 'parallel'
            : 'single-approver';
      toast.success(
        `Envelope sent — ${count} ${modeLabel} approver${count === 1 ? '' : 's'}${noteSuffix}`,
      );
      qc.invalidateQueries({ queryKey: ['binder-versions', binderId, documentId] });
      // We just caused the draft -> in_approval transition ourselves, so
      // update everything that displays it in one targeted shot instead of
      // waiting on a poll to notice: the open viewer modal (if this dialog
      // was launched from there), the folder table, and the routing panel
      // that's about to render for the first time now that approvers exist.
      qc.invalidateQueries({ queryKey: ['document-preview', binderId, documentId] });
      qc.invalidateQueries({ queryKey: ['binder', binderId] });
      qc.invalidateQueries({ queryKey: ['approval-routing', binderId, documentId] });
      // Close immediately — the toast already confirms who got the task;
      // no need to make the coordinator click through a second screen.
      onInitiated();
    },
    onError: (err) => {
      // Large documents can take DocuSeal a while to render/template — long
      // enough to occasionally exceed the API Gateway's hard 30s ceiling for
      // this call. The envelope creation itself isn't cancelled by that
      // timeout and typically finishes anyway; the version's own "already in
      // progress" guard means a retry can't double-send. Reassure instead of
      // alarming — no raw error, no "try again" implying nothing happened.
      if (err instanceof ApiError && err.status === 504) {
        toast.error(
          'This is taking longer than usual for a document this size. It may still complete in the background — close this dialog, wait a few seconds, then check the document status before sending again.',
          { duration: 10000 },
        );
        return;
      }
      toast.error(errorText(err, 'Failed to send envelope'));
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (initiate.isPending) return;
    initiate.mutate();
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center !m-0 bg-black/60 backdrop-blur-sm p-4 !m-0"
      onClick={(e) => {
        if (e.target === e.currentTarget && !initiate.isPending) onClose();
      }}
    >
      <form onSubmit={handleSubmit} className="card w-full max-w-lg space-y-4 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-ink-primary">{workflowName}</h2>
            <p className="mt-0.5 text-xs text-ink-muted">
              Sending <span className="font-medium text-ink-primary">{version.version_label}</span>{' '}
              ({version.file_name}) for signature.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={initiate.isPending}
            className="text-ink-muted hover:text-ink-primary disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-start gap-2 rounded border border-blue-100 bg-blue-50 p-3 text-xs text-blue-800">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Signers are resolved automatically from the approval rule for this document type and the
            role assignments on this study. You will see exactly who got the task in the
            confirmation toast after sending.
          </span>
        </div>

        <div>
          <label className="label text-xs" htmlFor="initiate-internal-note">
            Add an internal note (optional)
          </label>
          <textarea
            id="initiate-internal-note"
            className="input min-h-[72px]"
            maxLength={500}
            value={internalNote}
            placeholder="Visible to the requester only — included in the confirmation."
            disabled={initiate.isPending}
            onChange={(e) => setInternalNote(e.target.value)}
          />
        </div>

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={initiate.isPending}
          >
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={initiate.isPending}>
            {initiate.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Send envelope
          </button>
        </div>
      </form>
    </div>
  );
}
